import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { useAuth } from '../../firebase';

const BillingHistory = () => {
  const { t } = useTranslation('billing');
  const currentUser = useAuth();
  
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({ month: '' });

  const fetchHistory = useCallback(async (loadMore = false) => {
    if (!currentUser) return;
    
    if (loadMore) {
        setLoadingMore(true);
    } else {
        setLoading(true);
    }
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.month) params.append('month', filters.month);
      if (loadMore && lastVisible) params.append('lastVisible', lastVisible);

      const response = await apiClient.get(`/v1/billing-history?${params.toString()}`);

      if (!response.ok) throw new Error('Failed to fetch billing history');
      
      const data = await response.json();

      setHistory(prev => loadMore ? [...prev, ...data.history] : data.history);
      setLastVisible(data.lastVisible);
      setHasMore(data.history.length === 10 && data.lastVisible);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentUser, filters, lastVisible]);

  useEffect(() => {
    fetchHistory(false);
  }, [filters, currentUser]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
        fetchHistory(true);
    }
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({...prev, [name]: value }));
  }

  const getStatusStyle = (status) => {
    switch (status) {
      case 'paid':
      case 'succeeded':
        return 'text-green-600';
      case 'open':
      case 'pending':
        return 'text-yellow-600';
      case 'past_due':
      case 'failed':
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid':
      case 'succeeded': return t('status.paid');
      case 'open': return t('status.open');
      case 'past_due': return t('status.past_due');
      case 'void': return t('status.void');
      default: return status || '不明';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp._seconds ? new Date(timestamp._seconds * 1000) : new Date(timestamp);
    return date.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', hour12: false });
  };

  const getPlanNameOnly = (planText) => {
    if (!planText) return '-';
    
    // Check if it's an English key (free, lite, standard, business)
    const lower = planText.toLowerCase();
    const plansKeys = ['free', 'lite', 'standard', 'business'];
    
    if (plansKeys.includes(lower)) {
        return t(`plans.${lower}`);
    }

    // If it's already a translated name like "ライト" or has a prefix, clean it up
    return planText.replace('plans.', '');
  };

  return (
    <div className="bg-white p-8 w-full max-w-6xl mx-auto my-8 shadow-lg rounded-lg">
      <div className="flex justify-between items-center mb-6 pb-2 border-b-2 border-gray-200">
        <h1 className="text-3xl font-bold text-gray-900">{t('usageBillingHistory')}</h1>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">期間</label>
            <input type="month" name="month" value={filters.month} onChange={handleFilterChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
          </div>
        </div>
      </div>

      {loading && history.length === 0 ? (
        <p className="text-center py-4">{t('loading')}</p>
      ) : history.length === 0 ? (
        <p className="text-center py-4">{t('noBillingHistory')}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse table-auto">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left whitespace-nowrap">{t('billingDate')}</th>
                  <th className="border p-2 text-left">{t('plan')}</th>
                  <th className="border p-2 text-left">{t('overageCount')}</th>
                  <th className="border p-2 text-left">{t('amount')}</th>
                  <th className="border p-2 text-left whitespace-nowrap">{t('statusLabel')}</th>
                  <th className="border p-2 text-left">アクション</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="border p-2 whitespace-nowrap">{formatDate(item.processedAt)}</td>
                    <td className="border p-2">{getPlanNameOnly(item.plan_type || item.plan)}</td>
                    <td className="border p-2">{item.overageForBilling?.toLocaleString() || 0}</td>
                    <td className="border p-2">¥{(item.total || item.total_amount || 0).toLocaleString()}</td>
                    <td className={`border p-2 whitespace-nowrap font-semibold ${getStatusStyle(item.status)}`}>
                      {getStatusLabel(item.status)}
                    </td>
                    <td className="border p-2 whitespace-nowrap">
                      <div className="flex gap-4">
                        {(item.stripeInvoicePdfUrl || item.invoice_pdf) && (
                          <a href={item.stripeInvoicePdfUrl || item.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{t('download')}</a>
                        )}
                        {item.snapshot_id && (
                          <Link to={`/settings/billing-history/${item.snapshot_id}`} className="text-blue-600 hover:underline">{t('details')}</Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-center mt-6">
            {hasMore && (
              <button 
                onClick={handleLoadMore} 
                disabled={loadingMore} 
                className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-md disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {loadingMore ? t('loading') : t('loadMore')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BillingHistory;
